import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { recordHoldersAPI, uploadAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Plus } from 'lucide-react';
import HolderForm from './record-holders/HolderForm';
import HolderList from './record-holders/HolderList';
import { sanitizeRichTextHtml } from '../../lib/richText';

const DEFAULT_FORM = {
  name: '',
  title: 'SWAARNIM BOOK OF RECORDS\nRECORD HOLDER.',
  images: [],
  certificate_image: '',
  achievement: '',
  display_order: 0,
  published: true,
};

const ManageRecordHolders = () => {
  const [holders, setHolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingHolder, setEditingHolder] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [uploading, setUploading] = useState(false);

  const fetchHolders = useCallback(async () => {
    try {
      const data = await recordHoldersAPI.getAll({ includeUnpublished: true });
      setHolders(data);
    } catch (error) {
      console.error('Error fetching holders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHolders();
  }, [fetchHolders]);

  const handleImageUpload = async (e, type = 'record') => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      if (type === 'certificate') {
        const result = await uploadAPI.uploadImage(files[0], 'certificate');
        setFormData((prev) => ({ ...prev, certificate_image: result.path }));
      } else {
        const uploadPromises = files.map((file) => uploadAPI.uploadImage(file, 'record'));
        const results = await Promise.all(uploadPromises);
        setFormData((prev) => ({ ...prev, images: [...prev.images, ...results.map((r) => r.path)] }));
      }
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const removeCertificate = () => {
    setFormData((prev) => ({ ...prev, certificate_image: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        achievement: sanitizeRichTextHtml(formData.achievement),
      };
      if (editingHolder) {
        await recordHoldersAPI.update(editingHolder.id, payload);
      } else {
        await recordHoldersAPI.create(payload);
      }
      await fetchHolders();
      setFormData(DEFAULT_FORM);
      setEditingHolder(null);
      setShowForm(false);
      alert(editingHolder ? 'Record holder updated successfully!' : 'Record holder added successfully!');
    } catch (error) {
      console.error('Error adding holder:', error);
      alert('Failed to save holder');
    }
  };

  const handleEdit = (holder) => {
    setEditingHolder(holder);
    setFormData({
      name: holder.name || '',
      title: holder.title || DEFAULT_FORM.title,
      images: holder.images || [],
      certificate_image: holder.certificate_image || '',
      achievement: holder.achievement || '',
      display_order: holder.display_order ?? 0,
      published: holder.published ?? true,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingHolder(null);
    setFormData(DEFAULT_FORM);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record holder?')) return;
    try {
      await recordHoldersAPI.delete(id);
      await fetchHolders();
      alert('Record holder deleted successfully!');
    } catch (error) {
      console.error('Error deleting holder:', error);
      alert('Failed to delete holder');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64" data-testid="manage-holders-loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="manage-record-holders">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Manage Record Holders</h1>
          <Button
            onClick={() => {
              if (showForm) {
                resetForm();
              } else {
                setEditingHolder(null);
                setFormData(DEFAULT_FORM);
                setShowForm(true);
              }
            }}
            data-testid="toggle-holder-form"
            className="bg-red-700 hover:bg-red-800 flex items-center gap-2"
          >
            <Plus size={20} />
            Add New Holder
          </Button>
        </div>

        {showForm && (
          <HolderForm
            editing={Boolean(editingHolder)}
            formData={formData}
            setFormData={setFormData}
            uploading={uploading}
            onImageUpload={handleImageUpload}
            onRemoveImage={removeImage}
            onRemoveCertificate={removeCertificate}
            onSubmit={handleSubmit}
            onCancel={resetForm}
          />
        )}

        <HolderList holders={holders} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </AdminLayout>
  );
};

export default ManageRecordHolders;
